import { FieldValueLookupError } from '../../../common/errors';
import type { MultiCheckboxSetPublicValue } from '../../../common/fields/multi-checkbox';
import type { PublicToInternalPropertyTransformerContext } from '../../../common/types';
import type { OptionsVisibility } from '../../../common/types/options-visibility';
import { applyOptionsVisibilityRule } from '../../../common/utils/apply-options-visibility-rule';
import { getAvailableOptionsVisibilityRule } from '../../../common/utils/get-available-options-visibility-rule';
import { getResourceIdfromAri } from '../../../common/utils/get-resource-id-from-ari';
import { getValueLookupValuesDiff } from '../../../common/utils/get-value-lookup-values-diff';
import { multiValueLookup } from '../../../common/utils/multi-value-lookup';
import { transformOptionIdsToARI } from '../../../common/utils/options-visibility';

export type MultiCheckboxGetPublicValue = {
	id: string;
	value: string;
};

export type MultiCheckboxInternalAllowedValue = {
	label: string;
	value: string;
};

export type MultiCheckboxInternalValue = {
	label: string;
	value: string;
};

// TODO: remove this function after tyding the modern-issue-transition-fields-rollout-batch-2_bremb ff
export const oldTransformMultiCheckboxSetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: MultiCheckboxInternalAllowedValue[],
): MultiCheckboxInternalAllowedValue[] =>
	applyOptionsVisibilityRule(optionsVisibility, lookupValues, 'value');

export const transformMultiCheckboxSetOptionsVisibility = (
	{ options, isVisible }: OptionsVisibility,
	lookupValues: MultiCheckboxInternalAllowedValue[],
	context: PublicToInternalPropertyTransformerContext,
): MultiCheckboxInternalAllowedValue[] => {
	const aris = transformOptionIdsToARI(options, context);
	return applyOptionsVisibilityRule(
		{
			options: aris,
			isVisible,
		},
		lookupValues,
		'value',
	);
};

// when FF `modern-issue-transition-fields-rollout-batch-2_bremb` is removed. This function should be removed
export const transformMultiCheckboxGetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: MultiCheckboxInternalAllowedValue[],
): OptionsVisibility => getAvailableOptionsVisibilityRule(optionsVisibility, lookupValues, 'value');

// TODO: remove this function after tyding the modern-issue-transition-fields-rollout-batch-2_bremb ff
export const oldMultiCheckboxSetValueTransformer = (
	publicValue: MultiCheckboxSetPublicValue,
	allowedValues?: MultiCheckboxInternalAllowedValue[],
): MultiCheckboxInternalValue[] => {
	if (!publicValue.length) return [];

	const foundValue = multiValueLookup<MultiCheckboxInternalAllowedValue>(
		publicValue,
		allowedValues,
		'value',
	);

	if (foundValue.length !== publicValue.length) {
		const notFoundValues = getValueLookupValuesDiff(publicValue, foundValue, 'value');

		throw new FieldValueLookupError(notFoundValues.join());
	}

	return foundValue;
};

export const multiCheckboxSetValueTransformer = (
	publicValue: MultiCheckboxSetPublicValue,
	allowedValues: MultiCheckboxInternalAllowedValue[],
	context: PublicToInternalPropertyTransformerContext,
): MultiCheckboxInternalValue[] | null => {
	if (!publicValue.length) return [];
	const foundValue = multiValueLookup<MultiCheckboxInternalAllowedValue>(
		transformOptionIdsToARI(publicValue, context),
		allowedValues,
		'value',
	);
	if (foundValue.length !== publicValue.length) {
		const notFoundValues = getValueLookupValuesDiff(publicValue, foundValue, 'value');

		throw new FieldValueLookupError(notFoundValues.join());
	}

	return foundValue;
};

// TODO: remove this function after tyding the modern-issue-transition-fields-rollout-batch-2_bremb ff
export const oldMultiCheckboxGetValueTransformer = (
	internalValue: MultiCheckboxInternalValue[],
): MultiCheckboxGetPublicValue[] => {
	if (!internalValue) return [];

	return internalValue.map(({ value, label }) => ({
		id: value,
		value: label,
	}));
};

export const multiCheckboxGetValueTransformer = (
	internalValue: MultiCheckboxInternalValue[],
): MultiCheckboxGetPublicValue[] => {
	if (!internalValue) return [];
	return internalValue.map(({ value, label }) => ({
		id: getResourceIdfromAri(value),
		value: label,
	}));
};
